import React from "react";
import { GoX, GoSearch } from "react-icons/go";
import { FiArchive, FiRefreshCw } from "react-icons/fi";

import {
  ModalOverlay,
  IndexSummaryModalContainer,
  ActionButton,
} from "./styles";

function fmtDate(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

/**
 * IndexSummaryModal — modal compacto (640px) com visão geral do índice.
 *
 * Props:
 *  - index:     dados do índice (ativo ou arquivado)
 *  - stats:     { mappings, sampleDoc } carregado on-demand
 *  - archiving: bool — controla loading do botão arquivar
 *  - onClose:   fecha modal
 *  - onOpenDocs: abre o modal grande de docs
 *  - onArchive / onRestore: ações destrutivas (passam por ConfirmModal)
 */
export default function IndexSummaryModal({
  index,
  stats,
  archiving,
  onClose,
  onOpenDocs,
  onArchive,
  onRestore,
}) {
  if (!index) return null;

  const isArchived = index.isArchived;
  const healthClass = isArchived ? "archived" : index.health;

  // Mappings podem vir do stats (índice ativo) ou direto do index (arquivado)
  const mappings = isArchived
    ? index.mappings?.properties
    : stats?.mappings?.properties;

  const sampleDoc = isArchived ? index.sampleDocument : stats?.sampleDoc;

  const mappingsList = mappings ? Object.entries(mappings) : [];

  const subPrefix = isArchived ? "ÍNDICE ARQUIVADO" : "ÍNDICE ELASTICSEARCH";

  return (
    <ModalOverlay onClick={onClose}>
      <IndexSummaryModalContainer onClick={(e) => e.stopPropagation()}>
        {/* HEADER */}
        <div className="modal-header">
          <div className="modal-title">
            <span className="eyebrow">{subPrefix}</span>
            <h2 title={index.name}>{index.name}</h2>
            <span className={`modal-sub ${healthClass}`}>
              <span className="health-dot" />
              {isArchived
                ? `arquivado · ${
                    index.compressionRatio || "—"
                  } compressão`
                : `${index.health} · status ${index.status}`}
            </span>
          </div>
          <button className="modal-close" onClick={onClose} title="Fechar">
            <GoX />
          </button>
        </div>

        {/* BODY */}
        <div className="modal-body">
          {/* SECTION: Visão Geral */}
          <div className="section">
            <div className="section-eyebrow">Visão Geral</div>
            <div className="pairs-list">
              <div className="pair-row">
                <span className="pair-label">Documentos</span>
                <span className="pair-value brand">
                  {(index.docsCount || 0).toLocaleString("pt-BR")}
                </span>
              </div>
              <div className="pair-row">
                <span className="pair-label">
                  {isArchived ? "Tamanho Original" : "Tamanho"}
                </span>
                <span className="pair-value">{index.storeSize || "—"}</span>
              </div>
              {isArchived && (
                <>
                  <div className="pair-row">
                    <span className="pair-label">Snapshot</span>
                    <span className="pair-value">{index.snapshotSize || "—"}</span>
                  </div>
                  <div className="pair-row">
                    <span className="pair-label">Compressão</span>
                    <span className="pair-value brand">
                      {index.compressionRatio || "—"}
                    </span>
                  </div>
                </>
              )}
              <div className="pair-row">
                <span className="pair-label">Shards primários</span>
                <span className="pair-value">{index.priShards ?? 0}</span>
              </div>
              <div className="pair-row">
                <span className="pair-label">Réplicas</span>
                <span className="pair-value">{index.replicas ?? 0}</span>
              </div>
              <div className="pair-row">
                <span className="pair-label">Status</span>
                <span className={`status-pill ${index.status}`}>
                  {index.status}
                </span>
              </div>
              <div className="pair-row">
                <span className="pair-label">
                  {isArchived ? "Arquivado em" : "Último evento"}
                </span>
                <span className="pair-value">{fmtDate(index.refDate)}</span>
              </div>
            </div>
          </div>

          {/* SECTION: Schema (Mappings) */}
          <div className="section">
            <div className="section-eyebrow">Schema (Mappings)</div>
            {mappingsList.length > 0 ? (
              <div className="schema-table">
                <div className="schema-row head">
                  <span>Campo</span>
                  <span>Tipo</span>
                </div>
                {mappingsList.map(([field, config]) => (
                  <div key={field} className="schema-row">
                    <span className="field-name">{field}</span>
                    <span className="field-type">{config.type || "object"}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-inline">
                {isArchived
                  ? "Sem schema disponível neste backup"
                  : "Carregando schema..."}
              </div>
            )}
          </div>

          {/* SECTION: Sample Doc */}
          <div className="section">
            <div className="section-eyebrow">Documento de Amostra</div>
            {sampleDoc ? (
              <div className="json-preview">
                <pre>{JSON.stringify(sampleDoc, null, 2)}</pre>
              </div>
            ) : (
              <div className="empty-inline">
                {isArchived
                  ? "Sem amostra disponível neste backup"
                  : "Sem documento de amostra disponível"}
              </div>
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div className="modal-footer">
          <ActionButton onClick={onClose}>Fechar</ActionButton>

          {isArchived ? (
            <ActionButton $info onClick={onRestore}>
              <FiRefreshCw />
              Restaurar Índice
            </ActionButton>
          ) : (
            <ActionButton
              $danger
              onClick={onArchive}
              disabled={archiving}
            >
              <FiArchive />
              {archiving ? "Arquivando..." : "Arquivar Índice"}
            </ActionButton>
          )}

          <ActionButton $primary onClick={onOpenDocs}>
            <GoSearch />
            Ver Documentos
          </ActionButton>
        </div>
      </IndexSummaryModalContainer>
    </ModalOverlay>
  );
}
